import { FloorDef } from "../data/floorConfig";
import { EncounterEnemyConfig } from "./encounterEnemies";
import { SeededRng } from "../utils/rng";
import { EnemyId } from "./runState";
import { MAX_ENCOUNTER_ENEMIES } from "./encounterEnemies";
import { RunState } from "./runState";
import { BossId } from "../data/bosses";

/**
 * Generate encounters for a floor based on its configuration.
 * Uses seeded RNG for reproducible generation.
 *
 * @param floorConfig - Floor configuration (theme, enemy pool, encounter count range)
 * @param floor - Current floor number (for difficulty scaling)
 * @param rng - Seeded random number generator
 * @returns Array of encounter configurations ready for EncounterScreen
 */
export function generateFloorEncounters(
  floorConfig: FloorDef,
  floor: number,
  rng: SeededRng
): EncounterEnemyConfig[] {
  // Roll encounter count within the specified range
  const [minEncounters, maxEncounters] = floorConfig.encounterCountRange;
  const encounterCount = rng.nextInt(minEncounters, maxEncounters + 1);

  const encounters: EncounterEnemyConfig[] = [];

  for (let i = 0; i < encounterCount; i++) {
    // Determine number of enemies per encounter
    // Slow the ramp of enemy count to smooth difficulty:
    // 1 enemy floors 1-3, 2 enemies floors 4-6, 3 enemies from 7+
    const baseCount = Math.min(Math.max(1, Math.floor((floor - 1) / 3) + 1), MAX_ENCOUNTER_ENEMIES);

    // Randomly select enemies from the floor's enemy pool
    const enemies: Array<{ id: EnemyId; nameSuffix?: string }> = [];
    for (let j = 0; j < baseCount; j++) {
      const enemyId = rng.pick(floorConfig.enemyPool);
      // Add suffix if multiple enemies of same type
      const nameSuffix = baseCount > 1 ? `#${j + 1}` : undefined;
      enemies.push({ id: enemyId, nameSuffix });
    }

    encounters.push({ enemies });
  }

  return encounters;
}

/**
 * Generate a boss encounter based on the current floor configuration.
 * Boss is selected from the floor config's bossCandidate field and scaled to the current floor.
 *
 * @param run - Current run state (contains floor, currentFloorConfig)
 * @returns Encounter configuration with boss enemy, or null if floor config is missing
 */
export function generateBossEncounter(run: RunState): EncounterEnemyConfig | null {
  const floorConfig = run.currentFloorConfig;
  if (!floorConfig) {
    return null;
  }

  const bossId = floorConfig.bossCandidate as BossId;
  if (!bossId) {
    return null;
  }

  // Boss encounter contains a single boss enemy
  // The boss will be created by BossFactory when the encounter is built
  // EncounterEnemy.id can be EnemyId or BossId (updated type)
  return {
    enemies: [
      {
        id: bossId, // BossId is now accepted in EncounterEnemy type
        // Bosses don't use name suffixes (they have unique names)
      },
    ],
  };
}
